import { specialisationName } from '../../../constants/staff';

/**
 * ═══════════════════════════════════════════════════════════════════
 *  Prescription Practice Tour Steps
 * ═══════════════════════════════════════════════════════════════════
 *
 *  Configures the step-by-step guided tour for doctors learning
 *  to write digital prescriptions. Each step targets a DOM element
 *  via [data-tour="..."] attributes added to DigitalPrescriptionForm.
 *
 *  Steps use onEnter/onExit callbacks for page-specific side effects
 *  (auto-focus inputs, type sample text, etc.) without the tour engine
 *  knowing anything about the prescription form.
 */

/**
 * Build prescription tour steps array.
 *
 * @param {object} opts
 * @param {string} opts.specialization — doctor specialization (for contextual text)
 * @param {string} opts.sampleShorthand — e.g. "tab pcm 650 1-0-1 5d af" to demo in Rx input
 * @returns {Array<object>} steps array for TourProvider
 */
export function buildPrescriptionTourSteps({ specialization = '', sampleShorthand = '' } = {}) {
  // FE-35 — the speciality's own NAME. The title-caser stays only as the
  // fallback for a value with no label (it turns `MS_ENT` into "Ms Ent").
  const specLabel = specialisationName(specialization)
    || (specialization || 'General Physician')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return [
    // ── Step 0: What is a Digital Prescription? ─────────────────
    {
      id: 'intro-what',
      target: null, // centered modal
      title: '📄 What is a Digital Prescription?',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem' }}>
            A digital prescription replaces your handwritten pad with a
            <strong> structured, printable PDF</strong> — generated in seconds.
          </p>

          {/* ── Mini prescription mockup ───────────────────────── */}
          <div style={{
            border: '1px solid rgba(13,148,136,0.25)',
            borderRadius: '0.5rem',
            overflow: 'hidden',
            fontSize: '0.625rem',
            lineHeight: 1.5,
            marginBottom: '0.5rem',
            background: 'rgba(255,255,255,0.04)',
          }}>
            {/* Header */}
            <div style={{
              padding: '0.3rem 0.5rem',
              background: 'rgba(13,148,136,0.12)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontWeight: 700, color: '#0d9488' }}>
                🏥 Your Organisation Name
              </span>
              <span style={{ opacity: 0.6, fontSize: '0.5625rem' }}>
                Dr. {specLabel}
              </span>
            </div>

            {/* Patient row */}
            <div style={{
              padding: '0.25rem 0.5rem',
              display: 'flex',
              gap: '0.75rem',
              borderBottom: '1px dashed rgba(0,0,0,0.08)',
              opacity: 0.8,
            }}>
              <span>👤 Patient Name</span>
              <span>Age / Gender</span>
              <span>Date</span>
            </div>

            {/* Vitals */}
            <div style={{
              padding: '0.2rem 0.5rem',
              display: 'flex',
              gap: '0.5rem',
              opacity: 0.65,
              borderBottom: '1px dashed rgba(0,0,0,0.08)',
            }}>
              <span>BP: 120/80</span>
              <span>Temp: 98.6°F</span>
              <span>SpO₂: 98%</span>
            </div>

            {/* Rx section */}
            <div style={{ padding: '0.25rem 0.5rem' }}>
              <div style={{
                fontWeight: 700,
                color: '#0d9488',
                fontSize: '0.6875rem',
                marginBottom: '0.15rem',
              }}>
                ℞ Medicines
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', paddingLeft: '0.25rem' }}>
                <span>1. Tab Paracetamol 650mg — 1-0-1 × 5 days</span>
                <span>2. Tab Amoxicillin 500mg — 1-1-1 × 5 days</span>
              </div>
            </div>

            {/* Investigations + follow-up */}
            <div style={{
              padding: '0.2rem 0.5rem 0.3rem',
              display: 'flex',
              gap: '1rem',
              borderTop: '1px dashed rgba(0,0,0,0.08)',
              opacity: 0.7,
            }}>
              <span>🧪 Inv: CBC, LFT</span>
              <span>🔄 Follow-up: 5 Days</span>
            </div>
          </div>

          {/* ── Why it matters ─────────────────────────────────── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.25rem 0.5rem',
            fontSize: '0.6875rem',
            opacity: 0.8,
          }}>
            <span>✅ Clear & readable</span>
            <span>✅ Printable PDF</span>
            <span>✅ No illegible writing</span>
            <span>✅ Saves patient history</span>
          </div>
        </div>
      ),
      placement: 'center',
    },

    // ── Step 1: Welcome / Tour Overview ─────────────────────────
    {
      id: 'welcome',
      target: null, // centered modal
      title: '👋 Let\u2019s Write One Together!',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.5rem' }}>
            This 2-minute guided practice walks you through writing a digital prescription — step by step.
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
            ✦ You'll type real inputs into each section<br />
            ✦ At the end, you'll generate an actual PDF<br />
            ✦ Everything gets deleted after practice
          </p>
        </div>
      ),
      placement: 'center',
    },

    // ── Step 2: Patient Info ────────────────────────────────────
    {
      id: 'patient-info',
      target: '[data-tour="patient-info"]',
      title: '🧑 Enter Patient Details',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.25rem' }}>
            <strong>Your turn!</strong> Tap the fields below and enter:
          </p>
          <ul style={{ margin: '0.25rem 0', paddingLeft: '1.25rem', fontSize: '0.8125rem' }}>
            <li>Patient name</li>
            <li>Age &amp; Gender</li>
          </ul>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
            In real visits, reception usually fills these. Click <strong>Next</strong> when done.
          </p>
        </div>
      ),
      placement: 'bottom',
    },

    // ── Step 3: Vitals Strip ────────────────────────────────────
    {
      id: 'vitals',
      target: '[data-tour="vitals"]',
      title: '💓 Check the Vitals',
      content: 'These vitals are pre-filled for practice. In real visits, nurses record them before you see the patient. You can edit any value — try tapping one!',
      placement: 'bottom',
    },

    // ── Step 4: Chief Complaint ─────────────────────────────────
    {
      id: 'chief-complaint',
      target: '[data-tour="chief-complaint"]',
      title: '📋 Type the Chief Complaint',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.375rem' }}>
            <strong>Try it!</strong> Click the complaint field and type something like:
          </p>
          <div style={{
            background: 'rgba(13, 148, 136, 0.08)',
            borderRadius: '0.375rem',
            padding: '0.375rem 0.5rem',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: '#0d9488',
          }}>
            fever with headache for 3 days
          </div>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
            You'll see auto-suggestions from {specLabel} practice.
          </p>
        </div>
      ),
      placement: 'bottom',
    },

    // ── Step 5: Rx Input Bar ────────────────────────────────────
    {
      id: 'rx-input',
      target: '[data-tour="rx-input"]',
      title: '💊 Write a Prescription!',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.125rem', fontSize: '0.6875rem', color: '#60a5fa', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            👇 Scroll down to the Rx input below
          </p>
          <p style={{ margin: '0 0 0.375rem' }}>
            <strong>The magic input!</strong> Type medicine shorthand like you write on paper:
          </p>
          <div style={{
            background: 'rgba(13, 148, 136, 0.08)',
            borderRadius: '0.375rem',
            padding: '0.375rem 0.5rem',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: '#0d9488',
            marginBottom: '0.375rem',
          }}>
            {sampleShorthand || 'tab pcm 650 1-0-1 5d af'}
          </div>
          <p style={{
            margin: '0 0 0.25rem',
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            ⚠️ After typing, press{' '}
            <span style={{
              display: 'inline-block',
              padding: '0.125rem 0.4rem',
              borderRadius: '0.25rem',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}>
              Enter ↵
            </span>{' '}
            to add it!
          </p>
          <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.6 }}>
            Our AI instantly converts it to a structured prescription.
          </p>
        </div>
      ),
      placement: 'bottom',
    },

    // ── Step 6: Top Medicine Pills (Quick-Add) ──────────────────
    {
      id: 'top-medicines',
      target: '[data-tour="top-medicines"]',
      title: '⚡ Quick-Add Medicines',
      content: `Tap any pill to add that medicine instantly — no typing needed! These adapt to YOUR style as you write more prescriptions. We've pre-loaded common ${specLabel} medicines.`,
      placement: 'top',
      waitForSelector: true,
    },

    // ── Step 7: Templates ───────────────────────────────────────
    {
      id: 'templates',
      target: '[data-tour="templates"]',
      title: '📦 Try a Template',
      content: 'Templates save complete prescriptions — medicines, investigations, follow-up — all in one tap. Try tapping a template to auto-fill the form! You can create your own later.',
      placement: 'top',
      waitForSelector: true,
    },

    // ── Step 8: Lab Tests via Rx Input ────────────────────────────
    {
      id: 'investigations',
      target: '[data-tour="rx-input"]',
      title: '🧪 Add Tests to the Prescription',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.375rem' }}>
            <strong>Tests go in the same input.</strong> Type a test name:
          </p>
          <div style={{
            background: 'rgba(13, 148, 136, 0.08)',
            borderRadius: '0.375rem',
            padding: '0.375rem 0.5rem',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: '#0d9488',
            marginBottom: '0.25rem',
          }}>
            CBC
          </div>
          <p style={{
            margin: '0 0 0.25rem',
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            ⚠️ Type it and press{' '}
            <span style={{
              display: 'inline-block',
              padding: '0.125rem 0.4rem',
              borderRadius: '0.25rem',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}>
              Enter ↵
            </span>
          </p>
          <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.6 }}>
            Our AI detects whether it's a medicine or a test and adds it to the right section.
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.6875rem', opacity: 0.6 }}>
            Printed, not ordered — the test name goes on the prescription. Reception raises the lab orders.
          </p>
        </div>
      ),
      placement: 'bottom',
    },

    // ── Step 9: Follow-up ───────────────────────────────────────
    {
      id: 'follow-up',
      target: '[data-tour="follow-up"]',
      title: '🔄 Set Follow-up',
      content: 'When should the patient return? Tap a quick preset — 3 Days, 1 Week, 2 Weeks. This prints on the prescription and helps track patient returns.',
      placement: 'top',
    },

    // ── Step 10: Save Button ─────────────────────────────────────
    {
      id: 'save-btn',
      target: '[data-tour="save-btn"]',
      title: '🎯 Generate the Prescription!',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.375rem' }}>
            <strong>Final step!</strong> Click the <span style={{ color: '#0d9488', fontWeight: 700 }}>Save</span> button below to generate a real prescription PDF.
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
            You can also use <kbd style={{ background: 'rgba(0,0,0,0.06)', padding: '0.125rem 0.25rem', borderRadius: '0.25rem', fontSize: '0.6875rem' }}>Ctrl+Enter</kbd> to save quickly.
          </p>
        </div>
      ),
      placement: 'top',
    },

    // ── Step 11: Completion ─────────────────────────────────────
    {
      id: 'complete',
      target: null, // centered modal
      title: '🎉 You Did It!',
      content: () => (
        <div>
          <p style={{ margin: '0 0 0.5rem' }}>
            You just wrote your first digital prescription! Here's what you learned:
          </p>
          <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.25rem', fontSize: '0.8125rem' }}>
            <li>Shorthand input that AI converts instantly</li>
            <li>Quick-add pills adapted to your specialty</li>
            <li>One-tap templates for common cases</li>
            <li>Auto-generated PDF prescriptions</li>
          </ul>
          <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
            Click <strong>Finish</strong> to exit practice. Your real prescriptions will keep learning from your style!
          </p>
        </div>
      ),
      placement: 'center',
    },
  ];
}

/**
 * Get the sample shorthand string for a given specialization.
 * Used for the Rx input demo step.
 */
export function getSampleShorthand(specialization) {
  const samples = {
    GENERAL_PHYSICIAN: 'tab pcm 650 1-0-1 5d af',
    PEDIATRICIAN: 'syp pcm 250 5ml tds 3d af',
    GYNECOLOGIST: 'tab folic acid 5 1-0-0 30d af',
    ORTHOPEDIC: 'tab aceclofenac 100 1-0-1 5d af',
    DERMATOLOGIST: 'adapalene gel 0.1% apply hs 30d',
    DENTIST: 'cap amox 500 1-1-1 5d af',
    CARDIOLOGIST: 'tab atorvastatin 10 0-0-1 30d af',
    DIABETOLOGIST: 'tab metformin 500 1-0-1 30d af',
    ENT: 'tab levocetirizine 5 0-0-1 5d af',
    OPHTHALMOLOGIST: 'moxifloxacin eye drops 1 drop qid 7d',
    PULMONOLOGIST: 'tab montelukast 10 0-0-1 14d af',
    GASTROENTEROLOGIST: 'tab pan 40 1-0-0 14d bbf',
    NEUROLOGIST: 'tab pregabalin 75 0-0-1 14d af',
    PSYCHIATRIST: 'tab escitalopram 10 1-0-0 30d af',
  };
  return samples[(specialization || '').toUpperCase()] || samples.GENERAL_PHYSICIAN;
}
